import React, { useEffect, useRef, useState } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Customized,
} from "recharts";
import { Loader2, Calendar, ArrowLeft, Hash, Percent } from "lucide-react";
import api from "~/services/api";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";


const parseISO = (s) => new Date(s.replace(/-/g, "/"));
const format = (date, fmt) => {
  const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const MF = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  if (fmt === "MMM") return M[date.getMonth()];
  if (fmt === "MMMM yyyy") return `${MF[date.getMonth()]} ${date.getFullYear()}`;
  return date.toISOString();
};

const WhiteCard = ({ children }) => (
  <div className="relative">
    <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
      {children}
    </div>
    <div className="pointer-events-none absolute left-1/2 -bottom-[6px] -translate-x-1/2">
      <div className="relative">
        <div className="w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-gray-200" />
        <div className="absolute left-1/2 -top-[7px] -translate-x-1/2 w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[7px] border-t-white" />
      </div>
    </div>
  </div>
);

const TooltipCard = ({ children }) => (
  <div className="relative">
    <div className="rounded-xl border border-gray-200 bg-white text-gray-900 shadow-[0_6px_24px_rgba(0,0,0,0.08)] p-3">
      {children}
    </div>
    <div className="pointer-events-none absolute left-1/2 -bottom-[6px] -translate-x-1/2">
      <div className="relative">
        <div className="w-0 h-0 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-gray-200" />
        <div className="absolute left-1/2 -top-[7px] -translate-x-1/2 w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-t-[7px] border-t-white" />
      </div>
    </div>
  </div>
);

function useTooltipAnim(active) {
  const [first, setFirst] = useState(true);
  useEffect(() => {
    if (active) {
      const t = setTimeout(() => setFirst(false), 50);
      return () => clearTimeout(t);
    }
    setFirst(true);
  }, [active]);
  return first;
}

const CustomMonthTooltip = ({ active, payload }) => {
  const first = useTooltipAnim(active);
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className={`transition-all duration-200 ease-out ${first ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
      <WhiteCard>
        <p className="mb-1 font-semibold">{format(parseISO(d.month + "-01"), "MMMM yyyy")}</p>
        <p className="text-sm"><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 bg-[#403D8A]" />F1: <span className="font-medium">{(d.f1 ?? 0).toLocaleString()}</span></p>
        <p className="text-sm"><span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 bg-[#EE5827]" />Non-F1: <span className="font-medium">{(d.non_f1 ?? 0).toLocaleString()}</span></p>
        <div className="border-t border-gray-200 mt-2" />
        <p className="pt-1 text-xs font-semibold">Total: {(d.count ?? 0).toLocaleString()}</p>
        <p className="pt-1 text-xs text-gray-500">Click for reasons breakdown</p>
      </WhiteCard>
    </div>
  );
};

const CustomReasonTooltip = ({ active, payload }) => {
  const first = useTooltipAnim(active);
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className={`transition-all duration-200 ease-out ${first ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
      <TooltipCard>
        <p className="mb-1 font-semibold">{d.reason_name}</p>
        <p className="text-sm"><span className="font-medium">{(d.count ?? 0).toLocaleString()}</span> inactivations</p>
        <div className="border-t border-gray-200 mt-2" />
        <p className="pt-2 text-xs text-gray-500">Click for descriptions breakdown</p>
      </TooltipCard>
    </div>
  );
};

const CustomDescriptionTooltip = ({ active, payload }) => {
  const first = useTooltipAnim(active);
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className={`transition-all duration-200 ease-out ${first ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
      <TooltipCard>
        <p className="mb-1 font-semibold">{d.description_name}</p>
        <p className="text-sm"><span className="font-medium">{(d.count ?? 0).toLocaleString()}</span> inactivations</p>
        {d.description_id && (
          <>
            <div className="border-t border-gray-200 mt-2" />
            <p className="pt-2 text-xs text-gray-500">Click for options breakdown</p>
          </>
        )}
      </TooltipCard>
    </div>
  );
};

const CustomOptionTooltip = ({ active, payload }) => {
  const first = useTooltipAnim(active);
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className={`transition-all duration-200 ease-out ${first ? "opacity-0 translate-y-2" : "opacity-100 translate-y-0"}`}>
      <TooltipCard>
        <p className="mb-1 font-semibold">{d.option_name}</p>
        <p className="text-sm"><span className="font-medium">{(d.count ?? 0).toLocaleString()}</span> inactivations</p>
      </TooltipCard>
    </div>
  );
};

function MonthPillTickFactory({ activeIndex, hoveredIndex, onTickClick, onTickHover }) {
  return function MonthPillTick(props) {
    const { x, y, payload, index } = props;
    const date = parseISO(payload.value + "-01");
    const label = format(date, "MMM");
    const hot = activeIndex === index || hoveredIndex === index;
    const pillW = 46, pillH = 22, rx = 12;
    return (
      <g transform={`translate(${x},${y + 14})`} cursor="pointer"
        onClick={() => onTickClick?.(index)}
        onMouseEnter={() => onTickHover?.(index)}
        onMouseLeave={() => onTickHover?.(null)}
      >
        <rect x={-pillW / 2} y={-pillH + 2} width={pillW} height={pillH} rx={rx}
          fill={hot ? "#9F9ECA" : "#ffffff"} stroke={hot ? "#9F9ECA" : "#4C4C59"} opacity={hot ? 1 : 0.35} />
        <text textAnchor="middle" dy={-6} className="text-[12px] font-semibold" fill={hot ? "#111827" : "#4C4C59"}>
          {label}
        </text>
      </g>
    );
  };
}

function LabelMonth({ x, y, width, height, value }) {
  if (value === 0 || value === "0" || value == null) return null;
  const isFormatted = typeof value === "string";
  const display = isFormatted ? value : Number(value).toLocaleString();
  const numVal = isFormatted ? parseFloat(value) : Number(value);
  if (!numVal) return null;
  const inside = height >= 24;
  return (
    <text
      x={x + width / 2}
      y={inside ? y + height / 2 + 5 : y - 5}
      fill={inside ? "#FFFFFF" : "#111827"}
      textAnchor="middle" fontSize={11} fontWeight={600} pointerEvents="none"
    >
      {display}
    </text>
  );
}

function LabelSimple({ x, y, width, height, value }) {
  const isFormatted = typeof value === "string" && value.includes("%");
  const display = isFormatted ? value : Number(value ?? 0).toLocaleString();
  const isLow = height < 30;
  return (
    <text x={x + width / 2} y={isLow ? y - 8 : y + height - 12}
      textAnchor="middle" className="text-[14px] font-semibold"
      fill={isLow ? "#111827" : "#FFFFFF"} pointerEvents="none">
      {display}
    </text>
  );
}

function WrapTick({ x, y, payload, width: barWidth, maxCharsPerLine = 14 }) {
  const words = (payload?.value ?? "").split(" ");
  const lines = [];
  let current = "";
  for (const word of words) {
    if ((current + " " + word).trim().length > maxCharsPerLine && current.length > 0) {
      lines.push(current.trim());
      current = word;
    } else {
      current = (current + " " + word).trim();
    }
  }
  if (current) lines.push(current.trim());

  const lineHeight = 14;
  return (
    <g transform={`translate(${x},${y + 6})`}>
      {lines.map((line, i) => (
        <text key={i} x={0} y={i * lineHeight} textAnchor="middle" fontSize={12} fontWeight={600} fill="#1f2937">
          {line}
        </text>
      ))}
    </g>
  );
}

export default function InactivationBarChart({ year }) {
  // months
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [total, setTotal] = useState(0);

  // reasons
  const [viewMode, setViewMode] = useState("overview");
  const [reasonsData, setReasonsData] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedMonthTotal, setSelectedMonthTotal] = useState(0);

  // descriptions
  const [descriptionsData, setDescriptionsData] = useState([]);
  const [descriptionsLoading, setDescriptionsLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedReasonTotal, setSelectedReasonTotal] = useState(0);

  // options
  const [optionsData, setOptionsData] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState(null);
  const [selectedDescriptionTotal, setSelectedDescriptionTotal] = useState(0);

  const [showPercent, setShowPercent] = useState(false);

  const didAnimateMonths = useRef(false);
  const didAnimateReasons = useRef(false);
  const didAnimateDescriptions = useRef(false);
  const didAnimateOptions = useRef(false);

  const activeYear = typeof year === "number" ? year : new Date().getFullYear();
  const filial = useSelector((state) => state.auth?.filial);

  async function fetchData() {
    try {
      setLoading(true);
      const resp = await api.get("/inactivations/stats/monthly", { params: { year: activeYear } });
      const raw = resp.data || [];
      setData(raw.map((d) => ({
        month: d.month,
        f1: Number(d.f1 ?? 0),
        non_f1: Number(d.non_f1 ?? 0),
        count: Number(d.total ?? d.count ?? 0),
      })));
      setTotal(raw.reduce((acc, d) => acc + Number(d.total ?? d.count ?? 0), 0));
      didAnimateMonths.current = false;
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  async function fetchReasons(month) {
    try {
      setReasonsLoading(true);
      const resp = await api.get("/inactivations/stats/reasons", { params: { month } });
      const raw = resp.data || [];
      setReasonsData(raw.map((d) => ({
        reason_id: d.reason_id || null,
        reason_name: d.reason_name || "Unknown",
        count: Number(d.count ?? 0),
      })));
      didAnimateReasons.current = false;
    } catch {
      setReasonsData([]);
    } finally {
      setReasonsLoading(false);
    }
  }

  async function fetchDescriptions(month, reasonEntry) {
    try {
      setDescriptionsLoading(true);
      let resp;
      if (reasonEntry.reason_id) {
        resp = await api.get("/inactivations/stats/descriptions/new", {
          params: { month, reason_id: reasonEntry.reason_id },
        });
      } else {
        resp = await api.get("/inactivations/stats/descriptions/legacy", {
          params: { month, reason_name: reasonEntry.reason_name },
        });
      }
      const raw = resp.data || [];
      setDescriptionsData(raw.map((d) => ({
        description_id: d.description_id || null,
        description_name: d.description_name || "No description",
        count: Number(d.count ?? 0),
      })));
      didAnimateDescriptions.current = false;
    } catch {
      setDescriptionsData([]);
    } finally {
      setDescriptionsLoading(false);
    }
  }

  async function fetchOptions(month, descriptionEntry) {
    if (!descriptionEntry.description_id) {
      toast("No options for this column", { type: "info", autoClose: 2000 });
      return false;
    }
    try {
      setOptionsLoading(true);
      const resp = await api.get("/inactivations/stats/options", {
        params: { month, description_id: descriptionEntry.description_id },
      });
      const raw = resp.data || [];
      setOptionsData(raw.map((d) => ({
        option_id: d.option_id || null,
        option_name: d.option_name || "No option",
        count: Number(d.count ?? 0),
      })));
      didAnimateOptions.current = false;
      setViewMode("options");
      return true;
    } catch {
      setOptionsData([]);
      toast("It was not possible to load the options breakdown", {
        type: "error",
        autoClose: 3000,
      });
      return false;
    } finally {
      setOptionsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    setViewMode("overview");
    setSelectedMonth(null);
    setActiveIndex(null);
    didAnimateMonths.current = false;
  }, [year]);

  useEffect(() => {
    fetchData();
    setViewMode("overview");
    setSelectedMonth(null);
    setActiveIndex(null);
    setReasonsData([]);
    setDescriptionsData([]);
    setOptionsData([]);
    didAnimateMonths.current = false;
  }, [filial?.id]);

  function handleMonthClick(entry, index) {
    setActiveIndex(index);
    setSelectedMonth(entry.month);
    setSelectedMonthTotal(entry.count);
    fetchReasons(entry.month);
    setViewMode("reasons");
  }

  function handleReasonClick(reasonEntry) {
    if (!reasonEntry) return;
    setSelectedReason(reasonEntry);
    setSelectedReasonTotal(reasonEntry.count);
    fetchDescriptions(selectedMonth, reasonEntry);
    setViewMode("descriptions");
  }

  function handleDescriptionClick(descEntry) {
    if (!descEntry) return;
    setSelectedDescription(descEntry);
    setSelectedDescriptionTotal(descEntry.count);
    fetchOptions(selectedMonth, descEntry);
  }

  function handleBack() {
    if (viewMode === "options") {
      setViewMode("descriptions");
      setOptionsData([]);
      setSelectedDescription(null);
    } else if (viewMode === "descriptions") {
      setViewMode("reasons");
      setDescriptionsData([]);
      setSelectedReason(null);
    } else if (viewMode === "reasons") {
      setViewMode("months");
      setSelectedMonth(null);
      setActiveIndex(null);
      setReasonsData([]);
    } else if (viewMode === "months") {
      setViewMode("overview");
    }
  }

  const MonthPillTick = MonthPillTickFactory({
    activeIndex,
    hoveredIndex,
    onTickClick: (idx) => {
      const entry = data[idx];
      if (entry) handleMonthClick(entry, idx);
    },
    onTickHover: (idx) => setHoveredIndex(idx),
  });

  const isLoading = loading || reasonsLoading || descriptionsLoading || optionsLoading;

  const titleMap = {
    overview: "Inactivations",
    months: "Inactivations – By Month",
    reasons: "Inactivations – By Reason",
    descriptions: "Inactivations – By Description",
    options: "Inactivations – By Option",
  };

  // In overview the pill tick click goes to months detail, not reasons
  const OverviewPillTick = MonthPillTickFactory({
    activeIndex,
    hoveredIndex,
    onTickClick: (idx) => {
      const entry = data[idx];
      if (!entry) return;
      setActiveIndex(idx);
      setSelectedMonth(entry.month);
      setSelectedMonthTotal(entry.count);
      didAnimateMonths.current = false;
      setViewMode("months");
    },
    onTickHover: (idx) => setHoveredIndex(idx),
  });

  const currentTotal =
    viewMode === "overview" ? total :
    viewMode === "months" ? selectedMonthTotal :
    viewMode === "reasons" ? selectedMonthTotal :
    viewMode === "descriptions" ? selectedReasonTotal :
    selectedDescriptionTotal;

  const selectedMonthData = selectedMonth
    ? data.filter((entry) => entry.month === selectedMonth)
    : [];

  if (isLoading) {
    return (
      <div className="w-full p-3 px-5 border rounded-lg mt-4">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="animate-spin" size={36} />
        </div>
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="w-full p-3 px-5 border rounded-lg mt-4">
        <div className="flex flex-col items-center justify-center h-48 text-gray-500">
          <Calendar className="w-14 h-14 mb-2" />
          <p className="text-base">No inactivation data for {activeYear}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-3 px-5 border rounded-lg mt-4">
      {/* Header */}
      <div className="flex flex-row items-center justify-between mb-5">
        <div className="flex flex-col items-start justify-center">
          <div className="flex items-center gap-2">
            {viewMode !== "overview" && (
              <button onClick={handleBack} className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-bold">{titleMap[viewMode]}</h1>
            {(viewMode === "overview" || viewMode === "months" || viewMode === "reasons" || viewMode === "descriptions" || viewMode === "options") && (
              <div className="flex items-center ml-2 rounded-lg border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setShowPercent(false)}
                  className={`flex items-center px-2 py-1 text-xs font-semibold transition-colors ${!showPercent ? "bg-[#403D8A] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                  title="Show values"
                >
                  <Hash size={12} />
                </button>
                <button
                  onClick={() => setShowPercent(true)}
                  className={`flex items-center px-2 py-1 text-xs font-semibold transition-colors ${showPercent ? "bg-[#403D8A] text-white" : "bg-white text-gray-500 hover:bg-gray-50"}`}
                  title="Show percentages"
                >
                  <Percent size={12} />
                </button>
              </div>
            )}
          </div>
          {viewMode === "overview" && (
            <div className="flex items-center gap-2 text-base">
              <span className="text-gray-500">This year</span>
              <span className="text-gray-400">–</span>
              <span className="font-semibold text-gray-900">{activeYear}</span>
            </div>
          )}
          {viewMode === "months" && (
            <div className="flex items-center gap-4 text-base flex-wrap">
              <span className="font-semibold text-gray-900">{format(parseISO(selectedMonth + "-01"), "MMMM yyyy")}</span>
              <div className="flex items-center gap-3 ml-2">
                <span className="flex items-center gap-1 text-sm text-gray-700">
                  <span className="inline-block w-3 h-3 rounded-sm bg-[#403D8A]" /> F1
                </span>
                <span className="flex items-center gap-1 text-sm text-gray-700">
                  <span className="inline-block w-3 h-3 rounded-sm bg-[#EE5827]" /> Non-F1
                </span>
              </div>
            </div>
          )}
          {viewMode === "reasons" && (
            <p className="text-base font-extralight text-gray-500">
              {format(parseISO(selectedMonth + "-01"), "MMMM yyyy")}
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-bold text-gray-900">Total: {selectedMonthTotal.toLocaleString()}</span>
            </p>
          )}
          {viewMode === "descriptions" && (
            <p className="text-base font-extralight text-gray-500">
              {format(parseISO(selectedMonth + "-01"), "MMMM yyyy")}
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-semibold text-gray-700">{selectedReason?.reason_name}</span>
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-bold text-gray-900">Total: {selectedReasonTotal.toLocaleString()}</span>
            </p>
          )}
          {viewMode === "options" && (
            <p className="text-base font-extralight text-gray-500">
              {format(parseISO(selectedMonth + "-01"), "MMMM yyyy")}
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-semibold text-gray-700">{selectedReason?.reason_name}</span>
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-semibold text-gray-700">{selectedDescription?.description_name}</span>
              <span className="mx-1 text-gray-400">–</span>
              <span className="font-bold text-gray-900">Total: {selectedDescriptionTotal.toLocaleString()}</span>
            </p>
          )}
        </div>

        <div className="text-xl font-bold border-b border-l p-2 rounded-sm w-40">
          <h2 className="text-sm font-light text-gray-500">Total</h2>
          <span>{Number(currentTotal ?? 0).toLocaleString()}</span>
        </div>
      </div>

      {/* Chart area */}
      <ResponsiveContainer
        width="100%"
        height={viewMode === "descriptions" || viewMode === "options" ? 230 : 190}
      >
          {viewMode === "overview" ? (
            <BarChart data={data} barCategoryGap="80%" margin={{ top: 28, right: 0, bottom: 16, left: 0 }}>
              <Tooltip content={<CustomMonthTooltip />} position={{ y: -110 }} offset={-65} cursor={{ fill: "transparent" }} />
              <CartesianGrid vertical={false} strokeOpacity={0.15} />
              <XAxis dataKey="month" tick={<OverviewPillTick />} axisLine={false} tickLine={false} height={28} tickMargin={8} />
              <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} />
              <Bar dataKey="count" radius={[8, 8, 8, 8]}
                isAnimationActive={!didAnimateMonths.current} animationBegin={80} animationDuration={600} animationEasing="ease-out"
                onAnimationEnd={() => (didAnimateMonths.current = true)}
                onClick={(_, idx) => {
                  const entry = data[idx];
                  if (!entry) return;
                  setActiveIndex(idx);
                  setSelectedMonth(entry.month);
                  setSelectedMonthTotal(entry.count);
                  didAnimateMonths.current = false;
                  setViewMode("months");
                }}
                onMouseMove={(_, idx) => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <LabelList dataKey="count" isAnimationActive={false} content={(p) => {
                  const v = Number(p.value ?? 0);
                  const display = showPercent ? `${total > 0 ? ((v / total) * 100).toFixed(1) : 0}%` : v.toLocaleString();
                  return <LabelMonth {...p} value={display} />;
                }} />
                {data.map((_, i) => (
                  <Cell key={`ov-${i}`} cursor="pointer" fill={hoveredIndex === i || activeIndex === i ? "#5D5AAF" : "#403D8A"} />
                ))}
              </Bar>
            </BarChart>
          ) : viewMode === "months" ? (
            <BarChart data={selectedMonthData} barCategoryGap="20%" barGap={2} margin={{ top: 28, right: 0, bottom: 16, left: 0 }}>
              <Tooltip content={<CustomMonthTooltip />} position={{ y: -120 }} offset={-65} cursor={{ fill: "transparent" }} />
              <CartesianGrid vertical={false} strokeOpacity={0.15} />
              <XAxis dataKey="month" tick={<MonthPillTick />} axisLine={false} tickLine={false} height={28} tickMargin={8} />
              <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} />
              {/* F1 bar */}
              <Bar dataKey="f1" radius={[6, 6, 6, 6]}
                isAnimationActive={!didAnimateMonths.current} animationBegin={80} animationDuration={600} animationEasing="ease-out"
                onClick={(_, idx) => handleMonthClick(selectedMonthData[idx], idx)}
                onMouseMove={(_, idx) => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <LabelList dataKey="f1" isAnimationActive={false} content={(p) => {
                  const v = Number(p.value ?? 0);
                  const display = showPercent ? `${selectedMonthTotal > 0 ? ((v / selectedMonthTotal) * 100).toFixed(1) : 0}%` : v.toLocaleString();
                  return <LabelMonth {...p} value={display} />;
                }} />
                {selectedMonthData.map((_, i) => (
                  <Cell key={`f1-${i}`} cursor="pointer" fill={hoveredIndex === i || activeIndex === i ? "#5D5AAF" : "#403D8A"} />
                ))}
              </Bar>
              {/* Non-F1 bar */}
              <Bar dataKey="non_f1" radius={[6, 6, 6, 6]}
                isAnimationActive={!didAnimateMonths.current} animationBegin={80} animationDuration={600} animationEasing="ease-out"
                onAnimationEnd={() => (didAnimateMonths.current = true)}
                onClick={(_, idx) => handleMonthClick(selectedMonthData[idx], idx)}
                onMouseMove={(_, idx) => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <LabelList dataKey="non_f1" isAnimationActive={false} content={(p) => {
                  const v = Number(p.value ?? 0);
                  const display = showPercent ? `${selectedMonthTotal > 0 ? ((v / selectedMonthTotal) * 100).toFixed(1) : 0}%` : v.toLocaleString();
                  return <LabelMonth {...p} value={display} />;
                }} />
                {selectedMonthData.map((_, i) => (
                  <Cell key={`nf1-${i}`} cursor="pointer" fill={hoveredIndex === i || activeIndex === i ? "#F47A55" : "#EE5827"} />
                ))}
              </Bar>
              <Customized component={({ formattedGraphicalItems }) => {
                const f1Bars = formattedGraphicalItems?.[0]?.props?.data ?? [];
                const nf1Bars = formattedGraphicalItems?.[1]?.props?.data ?? [];
                return (
                  <g>
                    {f1Bars.map((f1Bar, i) => {
                      const nf1Bar = nf1Bars[i];
                      const monthTotal = selectedMonthData[i]?.count ?? 0;
                      if (!monthTotal || !f1Bar || !nf1Bar) return null;
                      const groupLeft = Math.min(f1Bar.x, nf1Bar.x);
                      const groupRight = Math.max(f1Bar.x + f1Bar.width, nf1Bar.x + nf1Bar.width);
                      const cx = (groupLeft + groupRight) / 2;
                      const topY = Math.min(f1Bar.y, nf1Bar.y) - 6;
                      const display = showPercent ? `${selectedMonthTotal > 0 ? ((monthTotal / selectedMonthTotal) * 100).toFixed(1) : 0}%` : monthTotal.toLocaleString();
                      return (
                        <text key={i} x={cx} y={topY} fill="#111827" textAnchor="middle" fontSize={13} fontWeight={700} pointerEvents="none">
                          {display}
                        </text>
                      );
                    })}
                  </g>
                );
              }} />
            </BarChart>
          ) : viewMode === "reasons" ? (() => {
            const sorted = [...reasonsData].sort((a, b) => b.count - a.count);
            return (
            <BarChart data={sorted} margin={{ top: 20, right: 0, bottom: 16, left: 0 }}>
              <Tooltip content={<CustomReasonTooltip />} position={{ y: -80 }} offset={-50} cursor={{ fill: "transparent" }} />
              <CartesianGrid vertical={false} strokeOpacity={0.15} />
              <XAxis dataKey="reason_name" tick={<WrapTick />} interval={0} axisLine={false} tickLine={false} height={60} />
              <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} domain={[0, (max) => Math.round(max * 1.1)]} axisLine={false} tickLine={false} />
              <Bar dataKey="count" radius={[8, 8, 8, 8]}
                isAnimationActive={!didAnimateReasons.current} animationBegin={80} animationDuration={600} animationEasing="ease-out"
                onAnimationEnd={() => (didAnimateReasons.current = true)}
                onClick={(_, idx) => handleReasonClick(sorted[idx])}
              >
                <LabelList dataKey="count" isAnimationActive={false} content={(p) => {
                  const val = Number(p.value ?? 0);
                  const display = showPercent
                    ? `${selectedMonthTotal > 0 ? ((val / selectedMonthTotal) * 100).toFixed(1) : 0}%`
                    : val.toLocaleString();
                  return <LabelSimple {...p} value={display} />;
                }} />
                {sorted.map((_, i) => (
                  <Cell key={`r-${i}`} cursor="pointer" fill="#EE5827" />
                ))}
              </Bar>
            </BarChart>
            );
          })() : viewMode === "descriptions" ? (() => {
            const sorted = [...descriptionsData].sort((a, b) => b.count - a.count);
            return (
            <BarChart data={sorted} margin={{ top: 20, right: 0, bottom: 16, left: 0 }}>
              <Tooltip content={<CustomDescriptionTooltip />} position={{ y: -80 }} offset={-50} cursor={{ fill: "transparent" }} />
              <CartesianGrid vertical={false} strokeOpacity={0.15} />
              <XAxis dataKey="description_name" tick={<WrapTick />} interval={0} axisLine={false} tickLine={false} height={80} />
              <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} domain={[0, (max) => Math.round(max * 1.1)]} axisLine={false} tickLine={false} />
              <Bar dataKey="count" radius={[8, 8, 8, 8]}
                isAnimationActive={!didAnimateDescriptions.current} animationBegin={80} animationDuration={600} animationEasing="ease-out"
                onAnimationEnd={() => (didAnimateDescriptions.current = true)}
                onClick={(_, idx) => handleDescriptionClick(sorted[idx])}
              >
                <LabelList dataKey="count" isAnimationActive={false} content={(p) => {
                  const val = Number(p.value ?? 0);
                  const display = showPercent
                    ? `${selectedReasonTotal > 0 ? ((val / selectedReasonTotal) * 100).toFixed(1) : 0}%`
                    : val.toLocaleString();
                  return <LabelSimple {...p} value={display} />;
                }} />
                {sorted.map((d, i) => (
                  <Cell key={`d-${i}`} cursor="pointer" fill={d.description_id ? "#403D8A" : "#9CA3AF"} />
                ))}
              </Bar>
            </BarChart>
            );
          })() : (
            <BarChart data={[...optionsData].sort((a, b) => b.count - a.count)} margin={{ top: 20, right: 0, bottom: 16, left: 0 }}>
              <Tooltip content={<CustomOptionTooltip />} position={{ y: -60 }} offset={-50} cursor={{ fill: "transparent" }} />
              <CartesianGrid vertical={false} strokeOpacity={0.15} />
              <XAxis dataKey="option_name" tick={<WrapTick />} interval={0} axisLine={false} tickLine={false} height={80} />
              <YAxis width={50} fontSize={12} tickFormatter={(v) => v.toLocaleString()} domain={[0, (max) => Math.round(max * 1.1)]} axisLine={false} tickLine={false} />
              <Bar dataKey="count" radius={[8, 8, 8, 8]}
                isAnimationActive={!didAnimateOptions.current} animationBegin={80} animationDuration={600} animationEasing="ease-out"
                onAnimationEnd={() => (didAnimateOptions.current = true)}
              >
                <LabelList dataKey="count" isAnimationActive={false} content={(p) => {
                  const val = Number(p.value ?? 0);
                  const display = showPercent
                    ? `${selectedDescriptionTotal > 0 ? ((val / selectedDescriptionTotal) * 100).toFixed(1) : 0}%`
                    : val.toLocaleString();
                  return <LabelSimple {...p} value={display} />;
                }} />
                {[...optionsData].sort((a, b) => b.count - a.count).map((_, i) => (
                  <Cell key={`o-${i}`} fill="#EE5827" />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
    </div>
  );
}
